import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import FormProviderRenderer from './FormProviderRenderer';
import { useFormDebug } from './FormDebugProvider';

// Create a context for form permissions
const FormSchemaContext = createContext({});

/**
 * FormSchemaProvider is a React context provider that supplies a form schema to its children.
 *
 * Props:
 * - schema: (object|null) The form schema to provide. If null, fetchSchema will be used.
 * - fetchSchema: (function|null) Async function to fetch the schema if schema is not provided.
 * - children: React nodes that will consume the schema context.
 *
 * Behavior:
 * - If schema is provided, it is used directly.
 * - If schema is not provided, fetchSchema is called to retrieve the schema.
 * - Displays a loading indicator while fetching.
 * - Displays error messages if schema retrieval fails.
 * - Provides the schema via FormSchemaContext to descendant components.
 */
export function FormSchemaProvider({
  schema = null,
  fetchSchema = null,
  children,
}) {
  const debug = useFormDebug();
  const [formSchema, setFormSchema] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialize = async () => {
    setLoading(true);
    try {
      if (schema === null) {
        if (fetchSchema === null || typeof fetchSchema !== 'function') {
          setErrors([
            {
              message:
                'Neither form schema variable nor schema fetcher function is defined',
            },
          ]);
        } else {
          const schemaFound = await fetchSchema();
          if (schemaFound.data) {
            setFormSchema(schemaFound?.data);
          } else if (schemaFound?.error) {
            setErrors(schemaFound?.error);
          } else {
            setErrors([
              {
                message: 'Could not fetch form schema',
              },
            ]);
          }
        }
      } else {
        setFormSchema(schema);
      }
    } catch (err) {
      setErrors([{ message: err?.message }]);
    }

    setLoading(false);
  };

  useEffect(() => {
    debug.addStep('FormSchema');
    initialize();
  }, [schema, fetchSchema]);

  const value = useMemo(() => ({ formSchema }), [formSchema]);

  return (
    <FormSchemaContext.Provider value={value}>
      <FormProviderRenderer
        message="Loading Schema"
        loading={loading}
        errors={errors}>
        {children}
      </FormProviderRenderer>
    </FormSchemaContext.Provider>
  );
}

export const useFormSchema = () => {
  return useContext(FormSchemaContext);
};
